import apiClient from '../api/interceptors';
import { ENDPOINTS } from '../api/endpoints';
import type { User } from '@/types';
import { clearStoredActiveTimeEntries } from '@/lib/activeTimeEntriesStorage';

/**
 * Authentication Service
 * Handles user authentication, token management, and session
 */

export interface LoginCredentials {
  username: string;
  password: string;
}

export interface LoginResponse {
  token: string;
  refreshToken: string;
  expiresIn: number;
  user: User;
}

export interface RefreshTokenRequest {
  refreshToken: string;
}

export interface RefreshTokenResponse {
  token: string;
  refreshToken: string;
  expiresIn: number;
}

/**
 * Authenticate user with username and password
 */
export const login = async (credentials: LoginCredentials): Promise<LoginResponse> => {
  const response = await apiClient.post<LoginResponse>(ENDPOINTS.AUTH.LOGIN, credentials);

  const { token, refreshToken, user } = response.data;

  // Store authentication data
  localStorage.setItem('token', token);
  localStorage.setItem('refreshToken', refreshToken);
  localStorage.setItem('user', JSON.stringify(user));

  return response.data;
};

/**
 * Logout current user
 */
export const logout = async (): Promise<void> => {
  try {
    await apiClient.post(ENDPOINTS.AUTH.LOGOUT);
  } catch (error) {
    // Continue with local logout even if API call fails
    console.error('Logout error:', error);
    } finally {
      try {
        clearStoredActiveTimeEntries();
        // Clear all authentication data
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
      localStorage.removeItem('user');

      // Redirect to login page
      window.location.href = '/login';
    } catch (cleanupError) {
      console.error('Error during logout:', cleanupError);
      // Force redirect anyway
      window.location.href = '/login';
    }
  }
};

/**
 * Refresh JWT token using refresh token
 */
export const refreshToken = async (refreshTokenValue: string): Promise<RefreshTokenResponse> => {
  const response = await apiClient.post<RefreshTokenResponse>(
    ENDPOINTS.AUTH.REFRESH,
    { refreshToken: refreshTokenValue }
  );

  const { token: newToken, refreshToken: newRefreshToken } = response.data;

  // Update stored tokens
  localStorage.setItem('token', newToken);
  localStorage.setItem('refreshToken', newRefreshToken);

  return response.data;
};

/**
 * Get current authenticated user details
 */
export const getCurrentUser = async (): Promise<User> => {
  const response = await apiClient.get<User>(ENDPOINTS.AUTH.ME);
  return response.data;
};

/**
 * Get stored JWT token from localStorage
 */
export const getStoredToken = (): string | null => {
  return localStorage.getItem('token');
};

/**
 * Get stored user from localStorage
 */
export const getStoredUser = (): User | null => {
  const userJson = localStorage.getItem('user');
  if (!userJson) return null;

  try {
    return JSON.parse(userJson);
  } catch {
    return null;
  }
};

/**
 * Check if user is authenticated (has valid token)
 */
export const isAuthenticated = (): boolean => {
  const token = getStoredToken();

  if (!token) return false;

  // Optional: Decode JWT and check expiration
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    const expiration = payload.exp * 1000; // Convert to milliseconds

    // Token is expired
    if (Date.now() >= expiration) {
      return false;
    }

    return true;
  } catch {
    // If can't decode token, consider it invalid
    return false;
  }
};
